/**
 * 推广分析相关的工具函数
 * 用于解析阿里妈妈推广数据并渲染为表格
 */

// HTML 转义
export const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// 解析推广数据的返回类型
export interface PromotionParseResult {
  markdown: string;
  jsonData: Record<string, string>[];
}

// 中文字段名到英文的映射
export const HEADER_TO_FIELD: Record<string, string> = {
  "日期": "date",
  "展现量": "impressions",
  "点击量": "clicks",
  "花费(元)": "cost",
  "点击率": "clickRate",
  "平均点击花费(元)": "avgClickCost",
  "千次展现花费(元)": "cpm",
  "总成交金额(元)": "totalTransactionAmount",
  "总成交笔数": "totalTransactionCount",
  "点击转化率": "clickConversionRate",
  "直接成交金额(元)": "directTransactionAmount",
  "间接成交金额(元)": "indirectTransactionAmount",
  "直接成交笔数": "directTransactionCount",
  "间接成交笔数": "indirectTransactionCount",
  "投入产出比": "roi",
  "含预售投产比": "roiWithPresale",
  "总成交成本(元)": "totalTransactionCost",
  "总预售成交金额(元)": "totalPresaleAmount",
  "总预售成交笔数": "totalPresaleCount",
  "直接预售成交金额(元)": "directPresaleAmount",
  "直接预售成交笔数": "directPresaleCount",
  "间接预售成交金额(元)": "indirectPresaleAmount",
  "间接预售成交笔数": "indirectPresaleCount",
  "拍下订单笔数": "orderCount",
  "拍下订单金额(元)": "orderAmount",
  "总购物车数": "totalCartCount",
  "直接购物车数": "directCartCount",
  "间接购物车数": "indirectCartCount",
  "加购率": "addToCartRate",
  "加购成本(元)": "addToCartCost",
  "收藏宝贝数": "productCollectionCount",
  "收藏店铺数": "shopCollectionCount",
  "总收藏数": "totalCollectionCount",
  "直接收藏宝贝数": "directProductCollectionCount",
  "间接收藏宝贝数": "indirectProductCollectionCount",
  "宝贝收藏率": "productCollectionRate",
  "宝贝收藏成本(元)": "productCollectionCost",
  "店铺收藏成本(元)": "shopCollectionCost",
  "总收藏加购数": "totalCollectionAndCartCount",
  "总收藏加购成本(元)": "totalCollectionAndCartCost",
  "宝贝收藏加购数": "productCollectionAndCartCount",
  "宝贝收藏加购成本(元)": "productCollectionAndCartCost",
  "引导访问量": "guidedVisitCount",
  "引导访问人数": "guidedVisitorCount",
  "引导访问潜客数": "guidedPotentialCustomerCount",
  "引导访问潜客占比": "guidedPotentialCustomerRate",
  "引导访问率": "guidedVisitRate",
  "深度访问量": "deepVisitCount",
  "平均访问页面数": "avgPageViews",
  "成交人数": "transactionCustomerCount",
  "成交新客数": "newCustomerCount",
  "成交新客占比": "newCustomerRate",
  "人均成交笔数": "avgTransactionPerCustomer",
  "人均成交金额": "avgAmountPerCustomer",
  "优惠券领取量": "couponClaimCount",
  "购物金充值笔数": "shoppingCreditRechargeCount",
  "购物金充值金额(元)": "shoppingCreditRechargeAmount",
  "旺旺咨询量": "wangwangConsultationCount",
  "自然流量转化金额": "organicTrafficAmount",
  "自然流量曝光量": "organicTrafficImpressions",
};

// 字段名列表（按原始数据顺序）- 仅作为无法从内容解析出表头时的兜底
const FIELD_NAMES = [
  "日期", "展现量", "点击量", "花费(元)", "点击率", "平均点击花费(元)",
  "总成交金额(元)", "总成交笔数", "点击转化率", "总购物车数", "收藏宝贝数",
  "收藏店铺数", "总收藏数", "宝贝收藏成本(元)", "千次展现花费(元)",
  "总预售成交金额(元)", "总预售成交笔数", "直接预售成交金额(元)", "直接预售成交笔数",
  "间接预售成交金额(元)", "间接预售成交笔数", "拍下订单笔数", "拍下订单金额(元)",
  "直接成交金额(元)", "间接成交金额(元)", "直接成交笔数", "间接成交笔数",
  "投入产出比", "含预售投产比", "总成交成本(元)", "引导访问潜客数", "成交人数",
  "人均成交笔数", "人均成交金额", "直接购物车数", "间接购物车数", "加购率",
  "店铺收藏成本(元)", "总收藏加购数", "总收藏加购成本(元)", "宝贝收藏加购数",
  "宝贝收藏加购成本(元)", "宝贝收藏率", "加购成本(元)", "直接收藏宝贝数",
  "间接收藏宝贝数", "优惠券领取量", "购物金充值笔数", "购物金充值金额(元)",
  "旺旺咨询量", "引导访问量", "引导访问人数", "引导访问潜客占比", "引导访问率",
  "深度访问量", "平均访问页面数", "成交新客数", "成交新客占比", "自然流量转化金额",
  "自然流量曝光量"
];

/** 将表头名转为英文字段名：已知映射用 HEADER_TO_FIELD，未知则保留原样 */
const headerToFieldKey = (header: string): string =>
  HEADER_TO_FIELD[header] ?? header;

/** 判断一个字符串是否像表头名（包含中文且长度合理，或在已知映射中） */
const isHeaderLike = (s: string): boolean => {
  if (!s) return false;
  if (HEADER_TO_FIELD[s] !== undefined) return true;
  // 包含中文字符且长度 ≤ 20（表头通常是短中文词组）
  if (s.length <= 20 && /[\u4e00-\u9fff]/.test(s)) return true;
  return false;
};

/**
 * 从第一块内容中解析表头（根据上传/粘贴内容动态获取表头）
 * 支持三种格式：
 *   1. 制表符分隔的单行表头（如 Excel 复制粘贴）
 *   2. 多空格 / 单空格分隔的单行表头
 *   3. 每行一个表头名（换行分隔，阿里妈妈"一键复制"的典型格式）
 * 若无法识别为表头则返回空数组
 */
const parseHeaderLine = (firstBlock: string): string[] => {
  const trimmed = firstBlock.trim();
  if (!trimmed) return [];

  const lines = trimmed.split(/[\r\n]+/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return [];

  const firstLine = lines[0];

  // --- Case 1: 单行，用制表符分隔 ---
  if (firstLine.includes("\t")) {
    const parts = firstLine.split(/\t/).map((s) => s.trim()).filter(Boolean);
    const hasKnown = parts.some((p) => HEADER_TO_FIELD[p] !== undefined);
    if (parts.length >= 2 && hasKnown) {
      return parts;
    }
  }

  // --- Case 2: 仅单行，用空格分隔 ---
  if (lines.length === 1) {
    let parts = firstLine.split(/\s{2,}/).map((s) => s.trim()).filter(Boolean);
    if (parts.length <= 1) {
      parts = firstLine.split(/\s+/).filter(Boolean);
    }
    const hasKnown = parts.some((p) => HEADER_TO_FIELD[p] !== undefined);
    if (parts.length >= 2 && hasKnown) {
      return parts;
    }
    return [];
  }

  // --- Case 3: 多行，每行一个表头名（换行分隔） ---
  const headerCandidates = lines.filter(isHeaderLike);
  const hasKnown = headerCandidates.some((p) => HEADER_TO_FIELD[p] !== undefined);
  // 至少 2 个且超过半数行是表头
  if (hasKnown && headerCandidates.length >= 2 && headerCandidates.length / lines.length >= 0.5) {
    return headerCandidates;
  }

  return [];
};

// 数值清洗：提取 +/- 前的核心数值
const cleanValue = (valueStr: string): string => {
  if (!valueStr || valueStr.trim() === "") return "";
  const trimmed = valueStr.trim();
  if (trimmed.toLowerCase() === "nan") return "NaN";

  // 匹配 +/- 变化指标前的数值部分
  const match = trimmed.match(/^(.+?)([+-]\d)/);
  if (match) {
    return match[1].trim();
  }
  return trimmed;
};

// 当换行符丢失时，使用正则分隔连在一起的数值
const splitValuesWithRegex = (block: string): string[] => {
  let normalized = block
    .replace(/[\u00a0\u3000]/g, " ")
    .replace(/[–—]/g, "-")
    // 在 % 后面、数字前面添加空格
    .replace(/%(\d)/g, "% $1")
    // 在 NaN 后面、数字前面添加空格
    .replace(/NaN(\d)/gi, "NaN $1")
    // 在小数后、千位分隔数字前添加空格
    .replace(/(\.\d+)(\d,\d{3})/g, "$1 $2")
    // 在两个小数连在一起时添加空格
    .replace(/(\.\d+)(\d+\.)/g, "$1 $2")
    // 在小数后（2位小数）、整数带变化指标前添加空格
    .replace(/(\.\d{2})(\d+[+-]\d+\.?\d*%)/g, "$1 $2")
    // 在千位分隔数字后、带变化指标的数字前添加空格
    .replace(/(,\d{3})(\d+[+-]\d+\.?\d*%)/g, "$1 $2");

  return normalized
    .split(/\s+/)
    .map(v => v.trim())
    .filter(v => v !== "");
};

/**
 * 解析推广表格数据
 * 表头根据粘贴内容第一行动态解析；若无法解析则回退到固定表头顺序
 * @param text 原始粘贴的文本
 * @returns 解析后的 Markdown 表格和 JSON 数据
 */
export const parsePromotionTableMarkdown = (text: string): PromotionParseResult => {
  // 按日期分割原始文本
  const rawText = (text || "").replace(/[\u00a0\u3000]/g, " ");
  const dateReg = /20\d{2}-\d{2}-\d{2}/g;
  const dates = rawText.match(dateReg) || [];

  if (dates.length === 0) {
    return { markdown: "", jsonData: [] };
  }

  const dataBlocks = rawText.split(dateReg).filter(block => block.trim() !== "");

  // 从第一块内容解析表头（根据上传内容动态获取）
  const parsedHeaders = dataBlocks.length > 0 ? parseHeaderLine(dataBlocks[0]) : [];
  const useDynamicHeaders = parsedHeaders.length >= 2;

  // 实际使用的表头顺序：动态解析成功则用解析结果，否则用固定 FIELD_NAMES
  const headerNames: string[] = useDynamicHeaders ? parsedHeaders : FIELD_NAMES;
  const startBlockIndex = useDynamicHeaders || (dataBlocks.length > 0 && dataBlocks[0].includes("展现量")) ? 1 : 0;

  // 数据列（不含日期）：用于按顺序映射数值
  const valueColumns = headerNames.filter((h) => h !== "日期");
  const expectedFieldCount = valueColumns.length;

  const data: Record<string, string>[] = [];

  dates.forEach((date, index) => {
    const blockIndex = index + startBlockIndex;
    if (blockIndex >= dataBlocks.length) return;

    const block = dataBlocks[blockIndex];

    let valueLines = block
      .split(/[\r\n]+/)
      .map(line => line.trim())
      .filter(line => line !== "");

    if (valueLines.length < expectedFieldCount) {
      valueLines = splitValuesWithRegex(block);
    }

    // 每个字段可能对应 2 行（当前值 + 对比值），取当前值
    const takeValueAtIndex = (i: number): string => {
      if (valueLines.length === expectedFieldCount * 2) {
        return valueLines[i * 2] ?? "";
      }
      return valueLines[i] ?? "";
    };

    const entry: Record<string, string> = {};
    entry[headerToFieldKey("日期")] = date;

    valueColumns.forEach((fieldName, fieldIndex) => {
      const rawValue = takeValueAtIndex(fieldIndex);
      const cleanedValue = cleanValue(rawValue);
      const key = headerToFieldKey(fieldName);
      entry[key] = cleanedValue;
    });

    data.push(entry);
  });

  // 展示表头：仅保留在数据中至少有一列有值的表头（顺序与 headerNames 一致）
  const displayFieldNames = headerNames.filter((fieldName) => {
    const key = headerToFieldKey(fieldName);
    return data.some((entry) => {
      const v = entry[key];
      return v !== undefined && String(v).trim() !== "";
    });
  });

  const headerRow = `| ${displayFieldNames.join(" | ")} |`;
  const separatorRow = `| ${displayFieldNames.map(() => "---").join(" | ")} |`;
  const dataRows = data.map((entry) => {
    const values = displayFieldNames.map((fieldName) => {
      const key = headerToFieldKey(fieldName);
      return entry[key] ?? "-";
    });
    return `| ${values.join(" | ")} |`;
  });

  const markdownTable = [headerRow, separatorRow, ...dataRows].join("\n");

  return {
    markdown: markdownTable,
    jsonData: data,
  };
};

/**
 * 将 Markdown 表格转换为 HTML 字符串
 * @param markdown Markdown 格式的表格
 * @returns HTML 字符串
 */
export const renderMarkdownToHtml = (markdown: string): string => {
  if (!markdown) {
    return "";
  }

  const lines = markdown.trim().split('\n');
  
  if (lines.length < 2) {
    return escapeHtml(markdown);
  }

  // 检查是否是表格格式（第二行应该是分隔符行，如 | --- | --- |）
  const separatorLine = lines[1];
  const separatorRegex = /^\|[\s-|]+\|$/;
  const isValidSeparator = separatorRegex.test(separatorLine.trim());
  
  if (!separatorLine || !isValidSeparator) {
    return escapeHtml(markdown);
  }

  // 解析表头
  const headerLine = lines[0];
  const headers = headerLine
    .split('|')
    .map(h => h.trim())
    .filter(h => h !== '');

  // 解析数据行
  const dataRows = lines.slice(2).map(line => {
    return line
      .split('|')
      .map(cell => cell.trim())
      .filter((_, idx, arr) => idx > 0 && idx < arr.length); // 去掉首尾空元素
  });

  // 生成 HTML
  const thStyle = 'border:1px solid #d9d9d9;padding:8px 12px;background-color:#fafafa;font-weight:600;text-align:left;white-space:nowrap;';
  const tdStyle = 'border:1px solid #d9d9d9;padding:6px 12px;text-align:left;white-space:nowrap;';

  let html = '<div style="overflow-x:auto;max-width:100%;">';
  html += '<table style="border-collapse:collapse;width:100%;font-size:12px;margin:8px 0;">';

  // 表头
  html += '<thead><tr>';
  headers.forEach(header => {
    html += `<th style="${thStyle}">${escapeHtml(header)}</th>`;
  });
  html += '</tr></thead>';

  // 数据行
  html += '<tbody>';
  dataRows.forEach(row => {
    html += '<tr>';
    headers.forEach((_, idx) => {
      const cellValue = row[idx] || '-';
      html += `<td style="${tdStyle}">${escapeHtml(cellValue)}</td>`;
    });
    html += '</tr>';
  });
  html += '</tbody></table></div>';

  return html;
};

/**
 * 从 HTML 表格中提取数据（用于获取用户编辑后的数据）
 * @param tableElement HTML 表格元素
 * @returns 提取的数据数组
 */
export const extractTableDataFromHtml = (tableElement: HTMLTableElement): Record<string, string>[] => {
  const result: Record<string, string>[] = [];
  
  // 获取表头
  const headerRow = tableElement.querySelector('thead tr');
  if (!headerRow) return result;
  
  const headers: string[] = [];
  headerRow.querySelectorAll('th').forEach(th => {
    headers.push(th.textContent?.trim() || '');
  });

  // 获取数据行（表头与 HEADER_TO_FIELD 一致时用英文 key，未知表头保留原表头名作为 key）
  const bodyRows = tableElement.querySelectorAll('tbody tr');
  bodyRows.forEach(row => {
    const entry: Record<string, string> = {};
    const cells = row.querySelectorAll('td');
    
    cells.forEach((cell, idx) => {
      const headerName = headers[idx];
      if (headerName == null) return;
      const fieldName = headerToFieldKey(headerName);
      const value = cell.textContent?.trim() || '';
      entry[fieldName] = value === '-' ? '' : value;
    });
    
    if (Object.keys(entry).length > 0) {
      result.push(entry);
    }
  });

  return result;
};

/**
 * 创建 markdownAnalyze 函数的工厂函数
 * @param onJsonDataChange 可选的回调函数，用于接收解析后的 JSON 数据
 * @returns markdownAnalyze 函数
 */
export const createMarkdownAnalyze = (
  onJsonDataChange?: (jsonData: Record<string, string>[]) => void
) => {
  return async (params: { text: string }) => {
    const result = parsePromotionTableMarkdown(params.text);
    
    // 如果提供了回调，通知外部 JSON 数据已更新
    if (onJsonDataChange) {
      onJsonDataChange(result.jsonData);
    }
    
    // 将 Markdown 表格渲染为 HTML
    const htmlContent = renderMarkdownToHtml(result.markdown);

    // 无有效数据行时必须返回空模块。消费组件以 modules 是否非空作为“解析成功”契约，
    // 不能把任意粘贴文本包装成一个可发送的空表格。
    if (result.jsonData.length === 0 || !htmlContent.includes('<table')) {
      return { modules: [] };
    }

    return {
      modules: [
        {
          title: "推广分析表格",
          content: htmlContent,
          markdown: result.markdown,
        },
      ],
    };
  };
};

/**
 * 构建推广分析发送数据的辅助函数
 * @param formData FormData 对象
 * @param tableElement 可选的表格元素（用于提取用户编辑后的数据）
 * @param fallbackJsonData 可选的原始 JSON 数据（作为 fallback）
 * @returns 格式化后的发送数据
 */
export const buildPromotionSendPayload = (
  formData: FormData,
  tableElement?: HTMLTableElement | null,
  fallbackJsonData?: Record<string, string>[] | null
): {
  grossProfitMargin: number;
  refundRate: number;
  tableData: Record<string, string>[];
} => {
  // 从 FormData 提取基本字段
  const grossProfitMargin = parseFloat(String(formData.get('grossProfitMargin') || '0')) || 0;
  const refundRate = parseFloat(String(formData.get('refundRate') || '0')) || 0;

  // 提取表格数据
  let tableData: Record<string, string>[] = [];
  
  if (tableElement) {
    const editedTableData = extractTableDataFromHtml(tableElement);
    if (editedTableData.length > 0) {
      tableData = editedTableData;
    }
  }
  
  // 如果没有从表格元素提取到数据，使用 fallback
  if (tableData.length === 0 && fallbackJsonData && fallbackJsonData.length > 0) {
    tableData = fallbackJsonData;
  }

  return {
    grossProfitMargin,
    refundRate,
    tableData,
  };
};
